import type { DesignTokens } from "../types/theme";

// Bóveda body font: Outfit (loaded in apps/web/index.html), with a robust
// system fallback chain so text stays readable if the webfont is slow/offline.
// (Display "Space Grotesk" and mono "JetBrains Mono" are applied via CSS
// utilities, not through these runtime tokens.) See docs/DESIGN.md.
const WEB_SYSTEM_FONT =
  "'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

/**
 * Base tokens for Light mode — Bóveda "acero claro": cool steel paper with a
 * deep-emerald action accent (sellado/seguro) and a sober gold warning.
 */
export const LIGHT_BASE_TOKENS: DesignTokens = {
  background: "#f4f6f9",
  foreground: "#1b2430",
  surface: "#ffffff",
  surfaceForeground: "#1b2430",
  surfaceSecondary: "#eef1f6",
  surfaceSecondaryForeground: "#1b2430",
  surfaceTertiary: "#e3e8f0",
  surfaceTertiaryForeground: "#1b2430",
  overlay: "#ffffff",
  overlayForeground: "#1b2430",
  muted: "#5c6775",
  default: "#eef1f6",
  defaultForeground: "#1b2430",
  accent: "#059669",
  accentForeground: "#ffffff",
  danger: "#e11d48",
  dangerForeground: "#ffffff",
  warning: "#c98a1e",
  warningForeground: "#1b2430",
  success: "#16a34a",
  successForeground: "#ffffff",
  separator: "#d3dae3",
  glassBackground: "rgba(255, 255, 255, 0.85)",
  glassBorder: "rgba(27, 36, 48, 0.1)",
  glassHighlight: "rgba(27, 36, 48, 0.08)",
  glow0: "rgba(5, 150, 105, 0.12)",
  glow1: "rgba(70, 110, 160, 0.1)",
  glow2: "rgba(40, 90, 170, 0.08)",
  mutedForeground: "#1b2430",
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(27, 40, 60, 0.12)",
  overlayShadow: "rgba(20, 35, 55, 0.2)",
  fieldShadow: "rgba(27, 40, 60, 0.06)",
  fieldBorder: "rgba(27, 40, 60, 0.12)",
  fieldPlaceholder: "rgba(27, 40, 60, 0.42)",
  fieldBackground: "rgba(27, 40, 60, 0.035)",
  segment: "#ffffff",
  segmentForeground: "#1b2430",
  link: "#059669",
  focus: "#059669",
  fieldForeground: "#1b2430",
  radius: "0.65rem",
  fontNormal: WEB_SYSTEM_FONT,
  fontMedium: WEB_SYSTEM_FONT,
  fontSemiBold: WEB_SYSTEM_FONT,
  fontBold: WEB_SYSTEM_FONT,
  border: "#d3dae3",
  primary: "#059669",
  primaryForeground: "#ffffff",
  secondary: "#eef1f6",
  secondaryForeground: "#1b2430",
  backdrop: "rgba(9, 14, 22, 0.5)",
};

/**
 * Base tokens for Dark mode — Bóveda "acero-noche": the sealed vault. Deep
 * blue-steel surfaces with a bright emerald keyhole accent and dark ink on top.
 */
export const DARK_BASE_TOKENS: DesignTokens = {
  background: "#0d1117",
  foreground: "#eef2f6",
  surface: "#161d27",
  surfaceForeground: "#eef2f6",
  surfaceSecondary: "#1c2531",
  surfaceSecondaryForeground: "#eef2f6",
  surfaceTertiary: "#26313f",
  surfaceTertiaryForeground: "#eef2f6",
  overlay: "#19212c",
  overlayForeground: "#eef2f6",
  muted: "#95a2b3",
  default: "#26313f",
  defaultForeground: "#eef2f6",
  accent: "#34d399",
  accentForeground: "#062a1e",
  danger: "#e5405f",
  dangerForeground: "#ffffff",
  warning: "#e3b341",
  warningForeground: "#241a06",
  success: "#22c55e",
  successForeground: "#04220f",
  separator: "#2f3a48",
  glassBackground: "rgba(13, 17, 23, 0.9)",
  glassBorder: "rgba(255, 255, 255, 0.12)",
  glassHighlight: "rgba(255, 255, 255, 0.22)",
  glow0: "rgba(52, 211, 153, 0.42)",
  glow1: "rgba(80, 120, 170, 0.4)",
  glow2: "rgba(45, 95, 190, 0.32)",
  mutedForeground: "#eef2f6",
  patternType: "none",
  patternOpacity: 10,
  surfaceShadow: "rgba(0, 0, 0, 0.5)",
  overlayShadow: "rgba(0, 0, 0, 0.6)",
  fieldShadow: "rgba(0, 0, 0, 0.3)",
  fieldBorder: "rgba(255, 255, 255, 0.12)",
  fieldPlaceholder: "rgba(255, 255, 255, 0.42)",
  fieldBackground: "rgba(255, 255, 255, 0.04)",
  segment: "#2b3543",
  segmentForeground: "#eef2f6",
  link: "#34d399",
  focus: "#34d399",
  fieldForeground: "#eef2f6",
  radius: "0.65rem",
  fontNormal: WEB_SYSTEM_FONT,
  fontMedium: WEB_SYSTEM_FONT,
  fontSemiBold: WEB_SYSTEM_FONT,
  fontBold: WEB_SYSTEM_FONT,
  border: "#2f3a48",
  primary: "#34d399",
  primaryForeground: "#062a1e",
  secondary: "#26313f",
  secondaryForeground: "#eef2f6",
  backdrop: "rgba(0, 0, 0, 0.7)",
};
